const getConfig = () => {
  const stored = localStorage.getItem('mailtracker_config');
  if (stored) {
    return JSON.parse(stored);
  }
  return {
    baseUrl: import.meta.env.VITE_API_URL || '',
    clientId: '',
    clientSecret: '',
  };
};

export const saveConfig = (config: {
  baseUrl: string;
  clientId: string;
  clientSecret: string;
}) => {
  localStorage.setItem('mailtracker_config', JSON.stringify(config));
};

export const getAuthHeaders = (): Record<string, string> => {
  const config = getConfig();
  return {
    'CF-Access-Client-Id': config.clientId,
    'CF-Access-Client-Secret': config.clientSecret,
    'Content-Type': 'application/json',
  };
};

export const getBaseUrl = (): string => {
  const config = getConfig();
  return config.baseUrl || '';
};

export interface Email {
  id: string;
  token: string;
  to_email: string | null;
  subject: string | null;
  source: 'dashboard' | 'gmail_extension';
  created_at: string;
  opened_at: string | null;
  open_count: number;
  click_count: number;
  link_count: number;
  last_open_at: string | null;
  status: string;
}

export interface ActivityEvent {
  id: string;
  email_id: string;
  type: string;
  created_at: string;
  provider_hint: string | null;
  to_email: string | null;
  subject: string | null;
  open_count: number;
  click_count: number;
  link_count: number;
  source: string;
  country: string | null;
  city: string | null;
  raw_ua: string | null;
}

export interface TopCountry {
  country: string;
  count: number;
}

export interface EmailEvent {
  id: string;
  type: 'open' | 'click';
  link_id: string | null;
  link_url: string | null;
  created_at: string;
  provider_hint: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  raw_ua: string | null;
}

export interface EmailEventsResponse {
  email: {
    id: string;
    to_email: string | null;
    subject: string | null;
    open_count: number;
    click_count: number;
    link_count: number;
    message_id: string | null;
  };
  events: EmailEvent[];
}

export interface Stats {
  totalOpens: number;
  uniqueEmails: number;
  todayOpens: number;
  topCountries: TopCountry[];
}

export async function createEmail(data: {
  to_email?: string;
  subject?: string;
  source: 'dashboard' | 'gmail_extension';
}): Promise<{
  emailId: string;
  token: string;
  pixelUrl: string;
  imgTag: string;
}> {
  const response = await fetch(`${getBaseUrl()}/api/emails`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to create tracking pixel');
  }

  return response.json();
}

export async function getEmails(): Promise<Email[]> {
  const response = await fetch(`${getBaseUrl()}/api/emails`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch emails');
  }

  const data = await response.json();
  return data.emails;
}

export async function getActivity(since?: string): Promise<ActivityEvent[]> {
  const url = since
    ? `${getBaseUrl()}/api/activity?since=${encodeURIComponent(since)}`
    : `${getBaseUrl()}/api/activity`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch activity');
  }

  const data = await response.json();
  return data.events;
}

export async function createReminder(
  emailId: string,
  days: number
): Promise<void> {
  const response = await fetch(`${getBaseUrl()}/api/reminders`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ emailId, days }),
  });

  if (!response.ok) {
    throw new Error('Failed to create reminder');
  }
}

export async function getStats(): Promise<Stats> {
  const response = await fetch(`${getBaseUrl()}/api/stats`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch stats');
  }

  return response.json();
}

export async function getEmailEvents(emailId: string): Promise<EmailEventsResponse> {
  const response = await fetch(`${getBaseUrl()}/api/emails/${emailId}/events`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch email events');
  }

  return response.json();
}

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString();
}
